import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Table from "../../../core/common/dataTable/index";
import {
  clearError as clearNocError,
  deleteNOCOnlineCourseRequest,
  getAllNocOnlineCourses,
  INocOnlineCourse,
  setCurrentRequest,
} from "../../../core/data/redux/nocOnlineCoursesSlice";
import {
  clearError as clearReimbursementError,
  getAllNocReimbursements,
  INocReimbursement,
  setCurrentReimbursement,
} from "../../../core/data/redux/nocReimbursementSlice";
import DeleteModal from "../../../core/modals/deleteModal";
import { AppDispatch, RootState } from "../../../core/data/redux/store";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import { all_routes } from "../../router/all_routes";
import SyncNocRequest from "./sync-noc-request";
import RequestStatusBadge from "./RequestStatusBadge";
import DateRangeFilter from "../../../core/common/DateRangeFilter";
import RequestStatusFilter from "./RequestStatusFilter";
import { isAdminOrSubAdmin, isSupportViewer } from "../../../utils/permissionUtils";

interface NOCOnlineCourseProps {}

const user: any = localStorage.getItem("user");
const userJson = user && user !== "undefined" ? JSON.parse(user) : {};
let userType: any = userJson?.user_type || userJson?.role;
const isManagement = isAdminOrSubAdmin(userType) || isSupportViewer(userType);

// ⚡ Consistent Date Format: DD-MM-YYYY
const formatDate = (dateString: string | null | undefined) => {
  if (!dateString || dateString === "-" || dateString === "00-00-0000" || dateString === "0000-00-00") return "-";
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) return dateString;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (e) {
    return dateString;
  }
};

const NOCOnlineCourse = (props: NOCOnlineCourseProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const routes = all_routes;

  // Active Tab: NOC vs REIMBURSEMENT
  const [activeTab, setActiveTab] = useState<"NOC" | "REIMBURSEMENT">("NOC");

  // Redux state for NOC Applications
  const {
    data: nocRequests,
    loading: nocLoading,
    error: nocError,
    total: nocTotal,
    deleteLoading,
  } = useSelector((state: RootState) => state.nocOnlineCoursesState);

  // Redux state for Reimbursement Requests
  const {
    data: reimbursementRequests,
    loading: reimbursementLoading,
    error: reimbursementError,
    total: reimbursementTotal,
  } = useSelector((state: RootState) => state.nocReimbursementState);

  // Local state
  const [deleteModalId, setDeleteModalId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [selectedStatus, setSelectedStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Update total count based on active tab
  useEffect(() => {
    const currentTotal = activeTab === "NOC" ? nocTotal : reimbursementTotal;
    setPagination((prev) => ({
      ...prev,
      total: currentTotal || 0,
    }));
  }, [nocTotal, reimbursementTotal, activeTab]);

  // Data fetching logic for NOC Applications
  const fetchNocData = (
    page = pagination.page,
    limit = pagination.limit,
    query = searchQuery,
    requestStatus = selectedStatus,
    dateStart = startDate,
    dateEnd = endDate
  ) => {
    const params: any = {
      page,
      limit,
      query,
      orderColumn: "created_at",
      orderBy: "DESC",
    };
    if (requestStatus) params.requestStatus = requestStatus;
    if (dateStart) params.startDate = dateStart;
    if (dateEnd) params.endDate = dateEnd;
    dispatch(getAllNocOnlineCourses(params));
  };

  // Data fetching logic for Reimbursement Requests
  const fetchReimbursementData = (
    page = pagination.page,
    limit = pagination.limit,
    query = searchQuery,
    requestStatus = selectedStatus,
    dateStart = startDate,
    dateEnd = endDate
  ) => {
    const params: any = {
      page,
      limit,
      query,
      orderColumn: "created_at",
      orderBy: "DESC",
    };
    if (requestStatus) params.requestStatus = requestStatus;
    if (dateStart) params.startDate = dateStart;
    if (dateEnd) params.endDate = dateEnd;
    dispatch(getAllNocReimbursements(params));
  };

  // Fetch data on tab or pagination change
  useEffect(() => {
    if (activeTab === "NOC") {
      fetchNocData();
    } else {
      fetchReimbursementData();
    }
  }, [activeTab, pagination.page, pagination.limit]);

  // Handle Tab Switch
  const handleTabChange = (tab: "NOC" | "REIMBURSEMENT") => {
    setActiveTab(tab);
    setSearchQuery("");
    setSelectedStatus("");
    setStartDate("");
    setEndDate("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Toast for Redux errors
  useEffect(() => {
    if (nocError) {
      toast.error(nocError);
      dispatch(clearNocError());
    }
    if (reimbursementError) {
      toast.error(reimbursementError);
      dispatch(clearReimbursementError());
    }
  }, [nocError, reimbursementError, dispatch]);

  // Handle pagination change
  const handlePagination = (newPagination: any) => {
    const page = newPagination.current || 1;
    const pageSize = newPagination.pageSize || pagination.limit;
    setPagination((prev) => ({ ...prev, page, limit: pageSize }));
    if (activeTab === "NOC") {
      fetchNocData(page, pageSize, searchQuery, selectedStatus, startDate, endDate);
    } else {
      fetchReimbursementData(page, pageSize, searchQuery, selectedStatus, startDate, endDate);
    }
  };

  const handleApplyFilter = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    if (activeTab === "NOC") {
      fetchNocData(1, pagination.limit, searchQuery, selectedStatus, startDate, endDate);
    } else {
      fetchReimbursementData(1, pagination.limit, searchQuery, selectedStatus, startDate, endDate);
    }
  };

  const handleClearFilter = () => {
    setSelectedStatus("");
    setStartDate("");
    setEndDate("");
    setPagination((prev) => ({ ...prev, page: 1 }));
    if (activeTab === "NOC") {
      fetchNocData(1, pagination.limit, searchQuery, "", "", "");
    } else {
      fetchReimbursementData(1, pagination.limit, searchQuery, "", "", "");
    }
  };

  // Handle search action
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPagination((prev) => ({ ...prev, page: 1 }));
    if (activeTab === "NOC") {
      fetchNocData(1, pagination.limit, query, selectedStatus, startDate, endDate);
    } else {
      fetchReimbursementData(1, pagination.limit, query, selectedStatus, startDate, endDate);
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = (id: number) => {
    setDeleteModalId(id);
  };

  // Handle delete confirmation response
  const handleDeleteConfirm = async (response: string) => {
    if (response === "YES" && deleteModalId) {
      try {
        await dispatch(deleteNOCOnlineCourseRequest({ id: deleteModalId })).unwrap();
        toast.success("NOC request deleted successfully!");
        fetchNocData(pagination.page, pagination.limit, searchQuery, selectedStatus, startDate, endDate);
      } catch (error) {
        console.error("Failed to delete request:", error);
        toast.error("Failed to delete NOC request. Please try again.");
      }
    }
    setDeleteModalId(null);
  };

  // Handle view details click
  const handleViewDetailsClick = (record: INocOnlineCourse) => {
    dispatch(setCurrentRequest(record.id));
  };

  // Handle edit button click
  const handleEditClick = (record: INocOnlineCourse) => {
    dispatch(setCurrentRequest(record.id));
  };

  const formatAmount = (fee: string | number | undefined | null) => {
    if (fee === undefined || fee === null || fee === "" || fee === "-") return "-";
    const num = parseFloat(String(fee).replace(/,/g, ""));
    return isNaN(num)
      ? String(fee)
      : num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Columns definition matching SAP NOC for Online Course: Previous NOC Requests
  const nocColumns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 70,
      render: (_: any, __: any, index: number) => (
        <div>
          {pagination.page
            ? index + pagination.page * pagination.limit - pagination.limit + 1
            : index + 1}
        </div>
      ),
    },
    {
      title: "Reference Number",
      dataIndex: "referenceNumber",
      sorter: (a: INocOnlineCourse, b: INocOnlineCourse) =>
        (a.referenceNumber || "").localeCompare(b.referenceNumber || ""),
      render: (text: string) => (
        <span className="fw-medium font-monospace">{text || "-"}</span>
      ),
    },
    {
      title: "Online Course Name",
      dataIndex: "onlineCourseName",
      sorter: (a: INocOnlineCourse, b: INocOnlineCourse) =>
        (a.onlineCourseName || "").localeCompare(b.onlineCourseName || ""),
      render: (text: string) => text || "-",
    },
    {
      title: "Course Fee",
      dataIndex: "courseFee",
      sorter: (a: INocOnlineCourse, b: INocOnlineCourse) =>
        parseFloat(String(a.courseFee || "0").replace(/,/g, "")) -
        parseFloat(String(b.courseFee || "0").replace(/,/g, "")),
      render: (fee: string | number) => formatAmount(fee),
    },
    {
      title: "Course Category",
      dataIndex: "course_category",
      sorter: (a: INocOnlineCourse, b: INocOnlineCourse) =>
        (a.course_category?.category_title || a.onlineCourseCategory || "").localeCompare(
          b.course_category?.category_title || b.onlineCourseCategory || ""
        ),
      render: (category: any, record: any) =>
        category?.category_title || record.onlineCourseCategory || "-",
    },
    {
      title: "Request Creation Date",
      dataIndex: "created_at",
      sorter: (a: INocOnlineCourse, b: INocOnlineCourse) =>
        new Date(a.created_at || a.requestCreationDate || 0).getTime() -
        new Date(b.created_at || b.requestCreationDate || 0).getTime(),
      render: (date: string, record: any) =>
        formatDate(date || record.requestCreationDate),
    },
    {
      title: "Request Status",
      dataIndex: "requestStatus",
      render: (status: string, record: any) => (
        <RequestStatusBadge status={status || record.status} />
      ),
    },
    {
      title: "Last/Latest Approver",
      dataIndex: "approverName",
      sorter: (a: any, b: any) =>
        (a.approverName || a.lastApprover || "").localeCompare(b.approverName || b.lastApprover || ""),
      render: (text: string, record: any) => {
        const name = text || record.lastApprover || record.approver?.name || record.approverName;
        const id = record.approverUserID || record.approverId || record.approver?.empId;
        if (name && id) return `${name} (${formatEmpIdForDisplay(id)})`;
        if (name) return name;
        if (id) return formatEmpIdForDisplay(id);
        return "-";
      },
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      render: (text: string, record: any) => {
        const val =
          text || record.hRemarks || record.HRemarks || record.remarksIfRejected || record.remarks1 || "-";
        return (
          <div className="text-truncate" style={{ maxWidth: "200px" }} title={val}>
            {val}
          </div>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (text: string, record: INocOnlineCourse) => {
        const isDraft = Boolean(
          record.is_draft ||
            (record as any).isDraft ||
            record.requestStatus === "DRAFT" ||
            record.requestStatus === "draft" ||
            record.requestStatus === "-" ||
            !record.requestStatus
        );

        const isApproved =
          record.requestStatus === "A" ||
          record.requestStatus === "Approved" ||
          record.requestStatus === "APPROVED" ||
          record.requestStatus?.toLowerCase() === "a";

        return (
          <div className="action-icon d-inline-flex align-items-center">
            <Link
              to={`${routes.noc.onlineCourse}/details/${record.id}`}
              className="text-primary me-2"
              title="View Details"
              onClick={() => handleViewDetailsClick(record)}
            >
              <i className="ti ti-eye fs-16"></i>
            </Link>

            {isDraft && (
              <>
                <Link
                  to={`${routes.noc.onlineCourse}/edit/${record.id}`}
                  className="text-success me-2"
                  title="Edit Request"
                  onClick={() => handleEditClick(record)}
                >
                  <i className="ti ti-edit fs-16"></i>
                </Link>
                <button
                  type="button"
                  className="btn btn-link text-danger p-0 me-2"
                  title="Delete Request"
                  onClick={() => handleDeleteClick(record.id)}
                  disabled={deleteLoading}
                  data-bs-toggle="modal"
                  data-bs-target="#delete_modal"
                >
                  <i className="ti ti-trash fs-16"></i>
                </button>
              </>
            )}

            {isApproved && (
              <Link
                to={`${routes.noc.onlineCourse}/create-reimbursement/${record.id}`}
                className="text-info"
                title="Create Reimbursement / Addition Request"
              >
                <i className="ti ti-receipt fs-16"></i>
              </Link>
            )}
          </div>
        );
      },
    },
  ];

  // Columns definition matching SAP NOC for Online Course: Previous Reimbursement/Addition Requests
  const reimbursementColumns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 70,
      render: (_: any, __: any, index: number) => (
        <div>
          {pagination.page
            ? index + pagination.page * pagination.limit - pagination.limit + 1
            : index + 1}
        </div>
      ),
    },
    {
      title: "Reference Number",
      dataIndex: "referenceNumber",
      sorter: (a: INocReimbursement, b: INocReimbursement) =>
        (a.referenceNumber || "").localeCompare(b.referenceNumber || ""),
      render: (text: string) => (
        <span className="fw-medium font-monospace">{text || "-"}</span>
      ),
    },
    {
      title: "Request Type",
      dataIndex: "claimOrOnlyAdditi",
      render: (type: string, record: any) => {
        if (type === "CL") return "Reimbursement & Addition";
        if (type === "AD") return "Only Addition";
        return record.reimbursement_type?.type_title || type || "-";
      },
    },
    {
      title: "Associated NOC Reference No.",
      dataIndex: "referenceNumber1",
      render: (text: string, record: any) => (
        <span className="font-monospace text-dark">{text || record.associatedNocRef || "-"}</span>
      ),
    },
    {
      title: "Request creation date",
      dataIndex: "created_at",
      sorter: (a: INocReimbursement, b: INocReimbursement) =>
        new Date(a.created_at || a.requestCreationDate || 0).getTime() -
        new Date(b.created_at || b.requestCreationDate || 0).getTime(),
      render: (date: string, record: any) =>
        formatDate(date || record.requestCreationDate),
    },
    {
      title: "Requested Amount",
      dataIndex: "requestAmount",
      render: (amount: string, record: INocReimbursement) => {
        if (record.claimOrOnlyAdditi === "AD") return "N/A";
        return formatAmount(amount);
      },
    },
    {
      title: "Approved Amount",
      dataIndex: "approvedAmount",
      render: (amount: string, record: INocReimbursement) => {
        if (record.claimOrOnlyAdditi === "AD") return "N/A";
        if (!amount || amount === "0.00" || amount === "0") return "-";
        return formatAmount(amount);
      },
    },
    {
      title: "Request Status",
      dataIndex: "requestStatus",
      render: (status: string, record: any) => (
        <RequestStatusBadge status={status || record.status} />
      ),
    },
    {
      title: "Last/Latest Approver",
      dataIndex: "approverName",
      sorter: (a: any, b: any) =>
        (a.approverName || a.lastApprover || "").localeCompare(b.approverName || b.lastApprover || ""),
      render: (text: string, record: any) => {
        const name = text || record.lastApprover || record.approver?.name || record.approverName;
        const id = record.approverUserID || record.approverId || record.approver?.empId;
        if (name && id) return `${name} (${formatEmpIdForDisplay(id)})`;
        if (name) return name;
        if (id) return formatEmpIdForDisplay(id);
        return "-";
      },
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      render: (text: string, record: any) => {
        const val =
          text || record.hRemarks || record.HRemarks || record.remarksIfRejected || record.remarks1 || "-";
        return (
          <div className="text-truncate" style={{ maxWidth: "200px" }} title={val}>
            {val}
          </div>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (text: string, record: INocReimbursement) => (
        <div className="action-icon d-inline-flex align-items-center">
          <Link
            to={`${routes.noc.onlineCourse}/reimbursement-details/${record.id}`}
            className="text-primary me-2"
            title="View Reimbursement Details"
            onClick={() => dispatch(setCurrentReimbursement(record))}
          >
            <i className="ti ti-eye fs-16"></i>
          </Link>
        </div>
      ),
    },
  ];

  // Clear errors on component unmount
  useEffect(() => {
    return () => {
      dispatch(clearNocError());
      dispatch(clearReimbursementError());
    };
  }, [dispatch]);

  const currentDataSource = activeTab === "NOC" ? nocRequests : reimbursementRequests;
  const currentLoading = activeTab === "NOC" ? nocLoading : reimbursementLoading;
  const currentColumns = activeTab === "NOC" ? nocColumns : reimbursementColumns;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">
              NOC/Reimbursement/Addition of Online Course
            </h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.main}>NOC Services</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Online Course
                </li>
              </ol>
            </nav>
          </div>
        </div>

        {/* Top Switcher: NOC Applications vs Reimbursement of Online Course */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <ul className="nav nav-pills bg-white p-1 rounded-3 shadow-sm border">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link px-4 py-2 fw-bold d-flex align-items-center gap-2 ${
                  activeTab === "NOC"
                    ? "active bg-primary text-white shadow-sm"
                    : "text-muted bg-transparent"
                }`}
                style={{ borderRadius: "8px", transition: "all 0.2s ease" }}
                onClick={() => handleTabChange("NOC")}
              >
                <i className="ti ti-certificate fs-16"></i>
                <span>NOC for Online Course</span>
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link px-4 py-2 fw-bold d-flex align-items-center gap-2 ${
                  activeTab === "REIMBURSEMENT"
                    ? "active bg-primary text-white shadow-sm"
                    : "text-muted bg-transparent"
                }`}
                style={{ borderRadius: "8px", transition: "all 0.2s ease" }}
                onClick={() => handleTabChange("REIMBURSEMENT")}
              >
                <i className="ti ti-receipt fs-16"></i>
                <span>Reimbursement of Online Course</span>
              </button>
            </li>
          </ul>
        </div>

        <div className="card">
          {/* Card Header: Title on Left, Action Buttons on Right */}
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-3 py-3">
            <h4 className="mb-0 fw-bold">
              {activeTab === "NOC"
                ? "NOC Applications"
                : "Reimbursement / Addition Requests"}
            </h4>
            <div className="d-flex align-items-center gap-2">
              {activeTab === "NOC" && !isManagement && (
                <Link
                  to={`${routes.noc.onlineCourse}/create`}
                  className="btn btn-primary text-nowrap"
                >
                  <i className="ti ti-plus me-1"></i>
                  Create New Request
                </Link>
              )}
              <SyncNocRequest
                nocType={"ONLINE-COURSE"}
                onSyncSuccess={() =>
                  activeTab === "NOC" ? fetchNocData() : fetchReimbursementData()
                }
              />
            </div>
          </div>

          <div className="card-body">
            {/* Dedicated Modern Filter Toolbar */}
            <div className="p-3 bg-light rounded-3 border mb-3">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <RequestStatusFilter
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                  />
                  <DateRangeFilter
                    startDate={startDate}
                    endDate={endDate}
                    onChangeStart={setStartDate}
                    onChangeEnd={setEndDate}
                    onApply={handleApplyFilter}
                    onClear={handleClearFilter}
                    variant="inline"
                  />
                </div>
              </div>
            </div>

            <Table
              columns={currentColumns}
              dataSource={currentDataSource}
              Selection={false}
              paginationConfig={{
                current: pagination.page,
                pageSize: pagination.limit,
                total: pagination.total || 0,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
                showTotal: (total: number, range: [number, number]) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
              handlePagination={handlePagination}
              loading={currentLoading}
              handleSearch={handleSearch}
            />
          </div>
        </div>

        {/* Delete Modal */}
        <DeleteModal handleDelete={handleDeleteConfirm} />
      </div>
    </div>
  );
};

export default NOCOnlineCourse;
